import React, {useEffect, useState} from 'react';
import {StyleProp, View, ViewStyle, StyleSheet, Platform} from 'react-native';

export interface WebPdfSource {
  uri: string;
  cache?: boolean;
  headers?: {[key: string]: string};
  cacheFileName?: string;
}

export interface WebPdfProps {
  source: WebPdfSource;
  style?: StyleProp<ViewStyle>;
  onLoadComplete?: (numberOfPages: number, filePath: string) => void;
  onError?: (error: Error) => void;
  onPressLink?: (uri: string) => void;
  trustAllCerts?: boolean;
  [key: string]: any;
}

const WebPdf: React.FC<WebPdfProps> = ({
  source,
  style,
  onLoadComplete,
  onError,
  onPressLink,
}) => {
  const [loaded, setLoaded] = useState(false);

  // Handle PDF load success
  const handleLoad = () => {
    setLoaded(true);
    if (onLoadComplete) {
      // We can't determine the number of pages in web implementation
      // so we pass 1 as a placeholder
      onLoadComplete(1, source.uri);
    }
  };

  // Handle PDF load error
  const handleError = () => {
    const error = new Error(`Failed to load PDF from ${source.uri}`);
    if (onError) {
      onError(error);
    }
  };

  // Listen for link clicks inside the PDF
  useEffect(() => {
    if (Platform.OS === 'web' && onPressLink) {
      const handler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const linkElement = target.closest('a');

        if (linkElement && linkElement.href) {
          event.preventDefault();
          onPressLink(linkElement.href);
        }
      };

      document.addEventListener('click', handler);
      return () => {
        document.removeEventListener('click', handler);
      };
    }
  }, [onPressLink]);

  if (Platform.OS !== 'web') {
    return null;
  }

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      ...(style as object),
    },
    pdfFrame: {
      width: '100%',
      height: '100%',
    },
    loadingContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
    },
    fallbackContainer: {
      padding: 20,
      textAlign: 'center',
    },
  });

  // Web-specific styles that can't be handled by React Native StyleSheet
  const webStyles = {
    iframeStyle: {
      ...(styles.pdfFrame as any),
      border: 'none',
    },
    objectStyle: {
      ...(styles.pdfFrame as any),
      display: 'none',
      position: 'absolute',
      opacity: 0,
    },
  };

  return (
    <View style={styles.container}>
      {/* Loading indicator */}
      {!loaded && (
        <div style={styles.loadingContainer as any}>
          <p>Loading PDF...</p>
        </div>
      )}

      {/* Primary PDF viewer (iframe) */}
      <iframe
        src={source.uri}
        style={webStyles.iframeStyle}
        title="PDF Viewer"
        onLoad={handleLoad}
        onError={handleError}
        allowFullScreen
      />

      {/* Fallback for browsers that don't support PDF embedding */}
      <object
        data={source.uri}
        type="application/pdf"
        style={webStyles.objectStyle}
        onLoad={handleLoad}
        onError={handleError}>
        <div style={styles.fallbackContainer as any}>
          <p>
            It appears your browser doesn't support embedded PDFs.
            <br />
            <a href={source.uri} target="_blank" rel="noopener noreferrer">
              Click here to download the PDF
            </a>
          </p>
        </div>
      </object>
    </View>
  );
};

export default WebPdf;
